import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSessionJwt } from '../../hooks/useSessionJwt';
import {
  fetchProblemRca,
  fetchProblemRcas,
  normalizeProblemRca,
  submitProblemRcaFeedback,
} from '../../services/problemRcaApi';
import RcaCard from './rca/RcaCard';
import RcaDetail from './rca/RcaDetail';
import NoteModal from './rca/NoteModal';
import { errorBannerStyle } from './rca/rcaHelpers';

export default function RcaSpaceTab({ appId, environment, selectedRange }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const { sessionId, getJwt } = useSessionJwt();
  const [rcas, setRcas] = useState([]);
  const [selectedRcaId, setSelectedRcaId] = useState(searchParams.get('rca_id') || '');
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState(null);
  const [detailError, setDetailError] = useState(null);
  const [actionBusy, setActionBusy] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [thumbsDownModalOpen, setThumbsDownModalOpen] = useState(false);
  const [actionNote, setActionNote] = useState('');
  const searchParamsString = searchParams.toString();

  const updateSelectedRca = useCallback((rcaId) => {
    setSelectedRcaId(rcaId);
    const next = new URLSearchParams(searchParamsString);
    next.set('tab', 'rca');
    if (rcaId) next.set('rca_id', rcaId);
    else next.delete('rca_id');
    next.delete('run_rca');
    next.delete('rca_classifier_key');
    next.delete('signal_label');
    next.delete('subtype');
    next.delete('event_key');
    next.delete('problem_id');
    if (next.toString() !== searchParamsString) {
      setSearchParams(next);
    }
  }, [searchParamsString, setSearchParams]);

  const loadRcas = useCallback(async (signal) => {
    const jwt = getJwt();
    if (!jwt || !appId || !environment) return;
    setLoading(true);
    setError(null);
    try {
      const response = await fetchProblemRcas(appId, environment, selectedRange, jwt, {
        signal,
        limit: 100,
      });
      setRcas(response.rcas.map(normalizeProblemRca).filter(Boolean));
    } catch (err) {
      if (err?.name !== 'AbortError') setError(err?.message || 'Failed to load RCAs');
    } finally {
      setLoading(false);
    }
  }, [appId, environment, sessionId, getJwt, selectedRange]);

  const loadDetail = useCallback(async (rcaId, signal) => {
    const jwt = getJwt();
    if (!jwt || !appId || !environment || !rcaId) {
      setDetail(null);
      return;
    }
    setDetailLoading(true);
    setDetailError(null);
    try {
      const rca = await fetchProblemRca(appId, environment, selectedRange, rcaId, jwt, {
        signal,
        limit: 100,
      });
      setDetail(normalizeProblemRca(rca));
    } catch (err) {
      if (err?.name !== 'AbortError') {
        setDetail(null);
        setDetailError(err?.message || 'Failed to load RCA detail');
      }
    } finally {
      setDetailLoading(false);
    }
  }, [appId, environment, sessionId, getJwt, selectedRange]);

  useEffect(() => {
    const abortController = new AbortController();
    loadRcas(abortController.signal);
    return () => abortController.abort();
  }, [loadRcas]);

  useEffect(() => {
    const urlRcaId = searchParams.get('rca_id') || '';
    if (urlRcaId && urlRcaId !== selectedRcaId) {
      setSelectedRcaId(urlRcaId);
    }
  }, [searchParams, selectedRcaId]);

  useEffect(() => {
    if (!selectedRcaId && rcas[0]?.rca_id) {
      updateSelectedRca(rcas[0].rca_id);
    }
  }, [rcas, selectedRcaId, updateSelectedRca]);

  useEffect(() => {
    const abortController = new AbortController();
    loadDetail(selectedRcaId, abortController.signal);
    return () => abortController.abort();
  }, [loadDetail, selectedRcaId]);

  const refreshSelected = useCallback(async () => {
    await loadRcas();
    if (selectedRcaId) await loadDetail(selectedRcaId);
  }, [loadDetail, loadRcas, selectedRcaId]);

  const selectedListRca = useMemo(
    () => rcas.find(rca => rca.rca_id === selectedRcaId) || null,
    [rcas, selectedRcaId],
  );

  const selectedDetail = detail || selectedListRca;

  const closeThumbsDownModal = useCallback(() => {
    if (actionBusy) return;
    setThumbsDownModalOpen(false);
    setActionNote('');
  }, [actionBusy]);

  const handleFeedback = useCallback(async (vote, note = '') => {
    const rcaId = selectedDetail?.rca_id || selectedRcaId;
    if (!rcaId || actionBusy) return false;
    setActionBusy(true);
    setActionError(null);
    try {
      await submitProblemRcaFeedback(appId, environment, rcaId, getJwt(), { vote, note });
      setThumbsDownModalOpen(false);
      setActionNote('');
      await refreshSelected();
      return true;
    } catch (err) {
      setActionError(err?.message || 'Failed to save RCA feedback');
      return false;
    } finally {
      setActionBusy(false);
    }
  }, [actionBusy, appId, environment, getJwt, refreshSelected, selectedDetail, selectedRcaId, selectedRange]);

  const handleSubmitThumbsDown = useCallback(() => {
    handleFeedback('thumbs_down', actionNote);
  }, [actionNote, handleFeedback]);

  if (!getJwt()) {
    return <div className="p-6 text-sm text-text-muted">Sign in to view RCAs.</div>;
  }

  return (
    <div className="flex min-h-[620px] flex-col gap-4 p-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold text-text-primary">RCA</h1>
          <p className="mt-1 max-w-3xl text-sm text-text-muted">
            Generated root-cause analyses for grouped classifier problems. Open problems without RCA stay in Overview.
          </p>
        </div>
        <button
          type="button"
          className="rounded border border-border-subtle px-3 py-2 text-sm text-text-primary hover:bg-background-tertiary"
          onClick={() => refreshSelected()}
        >
          Refresh
        </button>
      </div>

      {error && (
        <div className="rounded-lg border p-3 text-sm" style={errorBannerStyle()}>
          {error}
        </div>
      )}
      {actionError && (
        <div className="rounded-lg border p-3 text-sm" style={errorBannerStyle()}>
          {actionError}
        </div>
      )}

      <div className="grid min-h-0 flex-1 gap-4 lg:grid-cols-[360px_minmax(0,1fr)]">
        <aside className="min-h-0 rounded-xl border border-border-subtle bg-background-primary p-3">
          <div className="mb-3 flex items-center justify-between">
            <div className="text-sm font-semibold text-text-primary">RCAs</div>
            <div className="text-xs text-text-muted">{loading ? 'Loading...' : `${rcas.length} shown`}</div>
          </div>
          {loading && rcas.length === 0 ? (
            <div className="rounded-lg border border-border-subtle bg-background-secondary p-4 text-sm text-text-muted">
              Loading RCAs...
            </div>
          ) : rcas.length === 0 ? (
            <div className="rounded-lg border border-border-subtle bg-background-secondary p-4 text-sm text-text-muted">
              No generated RCAs found for this service and time range.
            </div>
          ) : (
            <div className="max-h-[720px] space-y-2 overflow-y-auto pr-1">
              {rcas.map((rca) => (
                <RcaCard
                  key={rca.rca_id}
                  rca={rca}
                  selected={rca.rca_id === selectedRcaId}
                  onSelect={updateSelectedRca}
                />
              ))}
            </div>
          )}
        </aside>

        <main className="min-h-0 rounded-xl border border-border-subtle bg-background-primary p-4">
          {detailLoading ? (
            <div className="rounded-lg border border-border-subtle bg-background-secondary p-4 text-sm text-text-muted">
              Loading RCA detail...
            </div>
          ) : detailError ? (
            <div className="rounded-lg border p-4 text-sm" style={errorBannerStyle()}>
              {detailError}
            </div>
          ) : selectedDetail ? (
            <RcaDetail
              rca={selectedDetail}
              appId={appId}
              environment={environment}
              selectedRange={selectedRange}
              jwt={getJwt()}
              actionBusy={actionBusy}
              onFeedback={handleFeedback}
              onThumbsDown={() => { setActionNote(''); setThumbsDownModalOpen(true); }}
              onResolution={() => {}}
              showReview
              showFeedback
              showResolution={false}
            />
          ) : (
            <div className="rounded-lg border border-border-subtle bg-background-secondary p-4 text-sm text-text-muted">
              Select an RCA to view details.
            </div>
          )}
        </main>
      </div>

      {thumbsDownModalOpen && (
        <NoteModal
          busy={actionBusy}
          note={actionNote}
          onNoteChange={setActionNote}
          onClose={closeThumbsDownModal}
          onSubmit={handleSubmitThumbsDown}
          title="Mark RCA as not useful?"
          description="Add an optional note so we can understand why this RCA was not useful."
          tone="danger"
        />
      )}
    </div>
  );
}
